import { useCallback, useEffect, useRef, useState } from 'react';
import { getCandiBankStats } from '../../../utils/api_service/candi_bank_api';
import { CandiBankStats } from '../../../utils/types/candi_bank_types';
import { FeatureCredits } from '../../../utils/types/credits_types';

/**
 * Headline numbers + this month's CV credits for the bank page.
 * `applyCredits` lets each upload response move the counter immediately; `refresh` re-reads
 * everything once a batch settles.
 */
export const useCandiBankStats = () => {
  const [stats, setStats] = useState<CandiBankStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const inFlight = useRef(false);
  const rerun = useRef(false);

  // A refresh asked for while one is in flight (upload settles during the slow mount fetch)
  // is not dropped: the running call goes round once more so the LAST response reflects the
  // bank after the upload, instead of the older response landing with pre-upload totals.
  const refresh = useCallback(async () => {
    if (inFlight.current) {
      rerun.current = true;
      return;
    }
    inFlight.current = true;
    try {
      do {
        rerun.current = false;
        try {
          const res = await getCandiBankStats();
          if (res.ok) {
            setStats(await res.json());
            setError(false);
          } else {
            setError(true);
          }
        } catch {
          setError(true);
        }
      } while (rerun.current);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  const applyCredits = useCallback((credits: FeatureCredits) => {
    setStats((prev) => (prev ? { ...prev, credits } : prev));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const credits = stats?.credits ?? null;
  const remaining = credits ? credits.remaining : null;
  const exhausted = !!credits && !credits.unlimited && (remaining ?? 0) <= 0;

  return { stats, credits, loading, error, refresh, applyCredits, exhausted };
};
